import { useRef } from "react";
import { Button, ButtonSize, ButtonVariant } from "shared/components/Button";
import { StateOverrideRow } from "./StateOverrideRow";
import { PropertyOverrideRow } from "./PropertyOverrideRow";
import { ENROLLMENT_TYPE_OPTIONS } from "../constants";
import { Select, Option } from "shared/components/forms/Select";
import { PlusSignIcon } from "@hugeicons-pro/core-stroke-rounded";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import type { OverrideSection } from "views/rent-reporting-settings";

export const EnrollmentSettingsForm = ({
  defaultEnrollmentType,
  onDefaultEnrollmentTypeChange,
  stateOverrides,
  propertyOverrides,
  availableStates,
  onOverrideAdd,
  onOverrideChange,
  onOverrideRemove,
  isLoadingStates,
  canAddStateOverride
}: {
  defaultEnrollmentType: string;
  onDefaultEnrollmentTypeChange: (value: string) => void;
  stateOverrides: { state: string; enrollmentType: string }[];
  propertyOverrides: { propertyId: string; enrollmentType: string }[];
  availableStates: Option[];
  onOverrideAdd: (section: OverrideSection) => void;
  onOverrideChange: (
    section: OverrideSection,
    index: number,
    field: string,
    value: string
  ) => void;
  onOverrideRemove: (section: OverrideSection, index: number) => void;
  isLoadingStates: boolean;
  canAddStateOverride: boolean;
}) => {
  const stateEndRef = useRef<HTMLDivElement>(null);
  const propertyEndRef = useRef<HTMLDivElement>(null);

  const handleAddState = () => {
    onOverrideAdd("state");
    stateEndRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "nearest"
    });
  };

  const handleAddProperty = () => {
    onOverrideAdd("property");
    propertyEndRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "nearest"
    });
  };

  return (
    <form
      id="rent-reporting-settings-form"
      onSubmit={(e) => {
        e.preventDefault();
      }}
    >
      <div className="space-y-6">
        <div className="space-y-4">
          <Select
            id="default-enrollment-type"
            label="Default enrollment type"
            placeholder="Select enrollment type"
            value={defaultEnrollmentType || "na"}
            onChange={(e) => onDefaultEnrollmentTypeChange(e.target.value)}
            options={ENROLLMENT_TYPE_OPTIONS}
            required
          />
        </div>
        <div className="border-t border-alpha-40 pt-6 space-y-4">
          <h3 className="text-sm font-medium text-content-700 mb-2">
            State overrides
          </h3>
          {stateOverrides.map((override, index) => (
            <StateOverrideRow
              key={index + override.state}
              override={override}
              index={index}
              availableStates={availableStates}
              onUpdate={(i, field, value) =>
                onOverrideChange("state", i, field, value)
              }
              onRemove={(i) => onOverrideRemove("state", i)}
            />
          ))}
          {defaultEnrollmentType === "na" ? (
            <Tooltip>
              <TooltipTrigger>
                <Button
                  type="button"
                  variant={ButtonVariant.BASE}
                  size={ButtonSize.SM}
                  onClick={handleAddState}
                  disabled={true}
                  HugeIcon={PlusSignIcon}
                >
                  {isLoadingStates ? "Loading states..." : "Add state override"}
                </Button>
              </TooltipTrigger>
              <TooltipContent className="max-w-[300px] rounded-lg z-[50] border bg-alpha-800 p-4 text-sm text-white">
                In order to add a state override, please add a default
                enrollment type
              </TooltipContent>
            </Tooltip>
          ) : (
            <Button
              type="button"
              variant={ButtonVariant.BASE}
              size={ButtonSize.SM}
              onClick={handleAddState}
              disabled={isLoadingStates || !canAddStateOverride}
              HugeIcon={PlusSignIcon}
            >
              {isLoadingStates ? "Loading states..." : "Add state override"}
            </Button>
          )}
          <div ref={stateEndRef} />
        </div>
        <div className="border-t border-alpha-40 pt-6 space-y-4">
          <div>
            <h3 className="text-sm font-medium text-content-700 mb-2">
              Property overrides
            </h3>
            <p className="text-xs text-content-300">
              Settings at the property level override the state default.
            </p>
          </div>
          {propertyOverrides.map((override, index) => (
            <PropertyOverrideRow
              key={index + override.propertyId}
              override={override}
              index={index}
              onUpdate={(i, field, value) =>
                onOverrideChange("property", i, field, value)
              }
              onRemove={(i) => onOverrideRemove("property", i)}
              excludePropertyIds={propertyOverrides.map((o) =>
                String(o.propertyId)
              )}
            />
          ))}
          {defaultEnrollmentType === "na" ? (
            <Tooltip>
              <TooltipTrigger>
                <Button
                  type="button"
                  variant={ButtonVariant.BASE}
                  size={ButtonSize.SM}
                  onClick={handleAddProperty}
                  disabled={true}
                  HugeIcon={PlusSignIcon}
                >
                  Add property override
                </Button>
              </TooltipTrigger>
              <TooltipContent className="max-w-[300px] rounded-lg z-[50] border bg-alpha-800 p-4 text-sm text-white">
                In order to add a property override, please add a default
                enrollment type
              </TooltipContent>
            </Tooltip>
          ) : (
            <Button
              type="button"
              variant={ButtonVariant.BASE}
              size={ButtonSize.SM}
              onClick={handleAddProperty}
              HugeIcon={PlusSignIcon}
            >
              Add property override
            </Button>
          )}
          <div ref={propertyEndRef} />
        </div>
      </div>
    </form>
  );
};
